// src/context/SocketContext.js
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { socketService } from '../services/socketService';
import { useAuth } from './AuthContext';
import { useNotificationContext } from './NotificationContext';

// Initial state
const initialState = {
  socket: null,
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  lastPing: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectInterval: 5000,
  onlineUsers: [],
  activeRooms: [],
  messageQueue: [], // For offline message queuing
  connectionQuality: 'good' // good, fair, poor
};

// Action types
const SocketActionTypes = {
  SET_SOCKET: 'SET_SOCKET',
  SET_CONNECTED: 'SET_CONNECTED',
  SET_CONNECTING: 'SET_CONNECTING',
  SET_CONNECTION_ERROR: 'SET_CONNECTION_ERROR',
  SET_LAST_PING: 'SET_LAST_PING',
  SET_RECONNECT_ATTEMPTS: 'SET_RECONNECT_ATTEMPTS',
  SET_ONLINE_USERS: 'SET_ONLINE_USERS',
  ADD_ONLINE_USER: 'ADD_ONLINE_USER',
  REMOVE_ONLINE_USER: 'REMOVE_ONLINE_USER',
  SET_ACTIVE_ROOMS: 'SET_ACTIVE_ROOMS',
  ADD_ACTIVE_ROOM: 'ADD_ACTIVE_ROOM',
  REMOVE_ACTIVE_ROOM: 'REMOVE_ACTIVE_ROOM',
  QUEUE_MESSAGE: 'QUEUE_MESSAGE',
  CLEAR_MESSAGE_QUEUE: 'CLEAR_MESSAGE_QUEUE',
  SET_CONNECTION_QUALITY: 'SET_CONNECTION_QUALITY',
  RESET_STATE: 'RESET_STATE'
};

// Reducer
const socketReducer = (state, action) => {
  switch (action.type) {
    case SocketActionTypes.SET_SOCKET:
      return {
        ...state,
        socket: action.payload
      };

    case SocketActionTypes.SET_CONNECTED:
      return {
        ...state,
        isConnected: action.payload,
        isConnecting: false,
        connectionError: action.payload ? null : state.connectionError,
        reconnectAttempts: action.payload ? 0 : state.reconnectAttempts
      };

    case SocketActionTypes.SET_CONNECTING:
      return {
        ...state,
        isConnecting: action.payload
      };

    case SocketActionTypes.SET_CONNECTION_ERROR:
      return {
        ...state,
        connectionError: action.payload,
        isConnecting: false
      };

    case SocketActionTypes.SET_LAST_PING:
      return {
        ...state,
        lastPing: action.payload
      };

    case SocketActionTypes.SET_RECONNECT_ATTEMPTS:
      return {
        ...state,
        reconnectAttempts: action.payload
      };

    case SocketActionTypes.SET_ONLINE_USERS:
      return {
        ...state,
        onlineUsers: action.payload
      };

    case SocketActionTypes.ADD_ONLINE_USER:
      return {
        ...state,
        onlineUsers: [...state.onlineUsers.filter(u => u.id !== action.payload.id), action.payload]
      };

    case SocketActionTypes.REMOVE_ONLINE_USER:
      return {
        ...state,
        onlineUsers: state.onlineUsers.filter(u => u.id !== action.payload)
      };

    case SocketActionTypes.SET_ACTIVE_ROOMS:
      return {
        ...state,
        activeRooms: action.payload
      };

    case SocketActionTypes.ADD_ACTIVE_ROOM:
      return {
        ...state,
        activeRooms: [...state.activeRooms.filter(r => r !== action.payload), action.payload]
      };

    case SocketActionTypes.REMOVE_ACTIVE_ROOM:
      return {
        ...state,
        activeRooms: state.activeRooms.filter(r => r !== action.payload)
      };

    case SocketActionTypes.QUEUE_MESSAGE:
      return {
        ...state,
        messageQueue: [...state.messageQueue, action.payload]
      };

    case SocketActionTypes.CLEAR_MESSAGE_QUEUE:
      return {
        ...state,
        messageQueue: []
      };

    case SocketActionTypes.SET_CONNECTION_QUALITY:
      return {
        ...state,
        connectionQuality: action.payload
      };

    case SocketActionTypes.RESET_STATE:
      return {
        ...initialState
      };

    default:
      return state;
  }
};

// Create context
const SocketContext = createContext();

// Socket provider component
export const SocketProvider = ({ children }) => {
  const [state, dispatch] = useReducer(socketReducer, initialState);
  const { user, isAuthenticated, token } = useAuth();
  const { addNotification } = useNotificationContext();

  // Initialize socket connection when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && token && !state.socket) {
      initializeSocket();
    } else if (!isAuthenticated && state.socket) {
      disconnectSocket();
    }

    return () => {
      if (state.socket) {
        state.socket.disconnect();
      }
    };
  }, [isAuthenticated, user, token]);

  // Initialize socket connection
  const initializeSocket = useCallback(() => {
    if (state.isConnecting || state.socket) return;

    dispatch({ type: SocketActionTypes.SET_CONNECTING, payload: true });

    try {
      const socket = socketService.connect(token, {
        userId: user.id,
        userRole: user.role,
        userName: user.name
      });

      dispatch({ type: SocketActionTypes.SET_SOCKET, payload: socket });
      setupSocketListeners(socket);
      
    } catch (error) {
      console.error('Socket initialization error:', error);
      dispatch({
        type: SocketActionTypes.SET_CONNECTION_ERROR,
        payload: error.message
      });
    }
  }, [user, token, state.isConnecting, state.socket]);

  // Setup socket event listeners
  const setupSocketListeners = (socket) => {
    // Connection events
    socket.on('connect', () => {
      console.log('Socket connected');
      dispatch({ type: SocketActionTypes.SET_CONNECTED, payload: true });
      
      // Send queued messages
      sendQueuedMessages();
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      dispatch({ type: SocketActionTypes.SET_CONNECTED, payload: false });
      
      // Attempt reconnection if not manual disconnect
      if (reason !== 'io client disconnect') {
        handleReconnection();
      }
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      dispatch({
        type: SocketActionTypes.SET_CONNECTION_ERROR,
        payload: error.message
      });
      handleReconnection();
    });

    // Ping/Pong for connection quality monitoring
    socket.on('pong', (latency) => {
      dispatch({ type: SocketActionTypes.SET_LAST_PING, payload: Date.now() });
      
      // Update connection quality based on latency
      let quality = 'good';
      if (latency > 100) quality = 'fair';
      if (latency > 300) quality = 'poor';
      
      dispatch({ type: SocketActionTypes.SET_CONNECTION_QUALITY, payload: quality });
    });

    // Healthcare-specific events
    setupHealthcareListeners(socket);
    
    // User presence events
    setupPresenceListeners(socket);
    
    // Notification events
    setupNotificationListeners(socket);
  };

  // Setup healthcare-specific socket listeners
  const setupHealthcareListeners = (socket) => {
    // Emergency alerts
    socket.on('emergency_alert', (data) => {
      addNotification({
        type: 'emergency',
        title: 'Emergency Alert',
        message: data.message,
        priority: 'critical',
        data: data
      });
    });

    // Appointment updates
    socket.on('appointment_update', (data) => {
      addNotification({
        type: 'appointment',
        title: 'Appointment Update',
        message: `Your appointment has been ${data.status}`,
        data: data
      });
    });

    // Consultation events
    socket.on('consultation_started', (data) => {
      addNotification({
        type: 'consultation',
        title: 'Consultation Started',
        message: `Your consultation with Dr. ${data.doctorName} has started`,
        data: data
      });
    });

    socket.on('consultation_ended', (data) => {
      addNotification({
        type: 'consultation',
        title: 'Consultation Ended',
        message: 'Your consultation has ended. Please check your prescriptions.',
        data: data
      });
    });

    // Order updates
    socket.on('order_status_update', (data) => {
      addNotification({
        type: 'order',
        title: 'Order Update',
        message: `Order #${data.orderNumber} is now ${data.status}`,
        data: data
      });
    });

    // Prescription updates
    socket.on('prescription_ready', (data) => {
      addNotification({
        type: 'prescription',
        title: 'Prescription Ready',
        message: `Your prescription is ready for pickup at ${data.pharmacyName}`,
        data: data
      });
    });

    // Driver/Ambulance updates
    socket.on('ambulance_dispatched', (data) => {
      addNotification({
        type: 'emergency',
        title: 'Ambulance Dispatched',
        message: `Ambulance is on the way. ETA: ${data.eta} minutes`,
        data: data
      });
    });

    socket.on('driver_location_update', (data) => {
      // Handle real-time driver location updates
      // This would typically update a map or tracking component
    });
  };

  // Setup user presence listeners
  const setupPresenceListeners = (socket) => {
    socket.on('user_online', (userData) => {
      dispatch({ type: SocketActionTypes.ADD_ONLINE_USER, payload: userData });
    });

    socket.on('user_offline', (userId) => {
      dispatch({ type: SocketActionTypes.REMOVE_ONLINE_USER, payload: userId });
    });

    socket.on('online_users_list', (users) => {
      dispatch({ type: SocketActionTypes.SET_ONLINE_USERS, payload: users });
    });
  };

  // Setup notification listeners
  const setupNotificationListeners = (socket) => {
    socket.on('new_notification', (notification) => {
      addNotification(notification);
    });

    socket.on('notification_read', (notificationId) => {
      // Handle notification read status update
    });
  };

  // Handle reconnection logic
  const handleReconnection = useCallback(() => {
    if (state.reconnectAttempts >= state.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      return;
    }

    const newAttempts = state.reconnectAttempts + 1;
    dispatch({ type: SocketActionTypes.SET_RECONNECT_ATTEMPTS, payload: newAttempts });

    setTimeout(() => {
      console.log(`Reconnection attempt ${newAttempts}`);
      if (state.socket) {
        state.socket.connect();
      }
    }, state.reconnectInterval * Math.pow(2, newAttempts - 1)); // Exponential backoff
  }, [state.reconnectAttempts, state.maxReconnectAttempts, state.reconnectInterval, state.socket]);

  // Send queued messages when connection is restored
  const sendQueuedMessages = () => {
    if (state.messageQueue.length > 0 && state.isConnected) {
      state.messageQueue.forEach(message => {
        emit(message.event, message.data);
      });
      dispatch({ type: SocketActionTypes.CLEAR_MESSAGE_QUEUE });
    }
  };

  // Emit event to server
  const emit = (event, data) => {
    if (state.isConnected && state.socket) {
      state.socket.emit(event, data);
    } else {
      // Queue message for later sending
      dispatch({
        type: SocketActionTypes.QUEUE_MESSAGE,
        payload: { event, data, timestamp: Date.now() }
      });
    }
  };

  // Join a room
  const joinRoom = (roomName) => {
    emit('join_room', { room: roomName, userId: user.id });
    dispatch({ type: SocketActionTypes.ADD_ACTIVE_ROOM, payload: roomName });
  };

  // Leave a room
  const leaveRoom = (roomName) => {
    emit('leave_room', { room: roomName, userId: user.id });
    dispatch({ type: SocketActionTypes.REMOVE_ACTIVE_ROOM, payload: roomName });
  };

  // Send a message to a room
  const sendMessage = (roomName, message) => {
    emit('room_message', {
      room: roomName,
      message: message,
      userId: user.id,
      userName: user.name,
      timestamp: Date.now()
    });
  };

  // Send emergency alert
  const sendEmergencyAlert = (emergencyData) => {
    emit('emergency_alert', {
      ...emergencyData,
      userId: user.id,
      userLocation: emergencyData.location,
      timestamp: Date.now()
    });
  };

  // Update user location (for drivers/ambulances)
  const updateLocation = (location) => {
    emit('location_update', {
      userId: user.id,
      location: location,
      timestamp: Date.now()
    });
  };

  // Send typing indicator
  const sendTyping = (roomName, isTyping = true) => {
    emit('typing', {
      room: roomName,
      userId: user.id,
      userName: user.name,
      isTyping: isTyping
    });
  };

  // Disconnect socket
  const disconnectSocket = () => {
    if (state.socket) {
      state.socket.disconnect();
      dispatch({ type: SocketActionTypes.RESET_STATE });
    }
  };

  // Force reconnection
  const forceReconnect = () => {
    dispatch({ type: SocketActionTypes.SET_RECONNECT_ATTEMPTS, payload: 0 });
    if (state.socket) {
      state.socket.disconnect();
      state.socket.connect();
    } else {
      initializeSocket();
    }
  };

  // Get connection status
  const getConnectionStatus = () => {
    if (state.isConnected) return 'connected';
    if (state.isConnecting) return 'connecting';
    if (state.connectionError) return 'error';
    return 'disconnected';
  };

  // Check if user is online
  const isUserOnline = (userId) => {
    return state.onlineUsers.some(user => user.id === userId);
  };

  const value = {
    ...state,
    emit,
    joinRoom,
    leaveRoom,
    sendMessage,
    sendEmergencyAlert,
    updateLocation,
    sendTyping,
    disconnectSocket,
    forceReconnect,
    getConnectionStatus,
    isUserOnline
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

// Custom hook to use socket context
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;